import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputActionMeta } from "react-select";
import { useGetCitiesPaginatedLazyQuery } from "@me-team/host/main/countryGeo/graphql/countryGeo.hooks";
import type { CityProxy } from "@me-team/host/main/graphql/types";
import { CUSTOM_CITY_OPTION_VALUE } from "../constants/city";
import type { Option } from "../interfaces/interfaces";

type CityEntity = Pick<CityProxy, "id" | "name">;

interface UseCountryCitiesPaginatedOptionsParams {
    countryId?: number | null;
    selectedCity?: CityEntity | null;
    includeCustomCityOption?: boolean;
    customCityOptionLabel: string;
    customCityOptionValue?: number;
    itemsPerPage?: number;
    skip?: boolean;
}

const FIRST_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_DELAY = 400;

const mapCityToOption = (city: CityEntity): Option => ({
    value: city.id,
    label: city.name,
});

const mergeUniqueOptions = (options: Option[]): Option[] => {
    const optionMap = new Map<number, Option>();

    options.forEach((option) => {
        optionMap.set(option.value, option);
    });

    return Array.from(optionMap.values());
};

const isCustomCityOptionValue = (value: number, customCityOptionValue: number) => value === customCityOptionValue;

const appendCustomCityOption = (
    options: Option[],
    includeCustomCityOption: boolean,
    customCityOptionLabel: string,
    customCityOptionValue: number
) => {
    const optionsWithoutCustomCity = options.filter(
        (option) => !isCustomCityOptionValue(option.value, customCityOptionValue)
    );

    if (!includeCustomCityOption) return optionsWithoutCustomCity;

    return optionsWithoutCustomCity.concat({
        value: customCityOptionValue,
        label: customCityOptionLabel,
    });
};

export const useCountryCitiesPaginatedOptions = ({
    countryId,
    selectedCity,
    includeCustomCityOption = true,
    customCityOptionLabel,
    customCityOptionValue = CUSTOM_CITY_OPTION_VALUE,
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
    skip = false,
}: UseCountryCitiesPaginatedOptionsParams) => {
    const [getCitiesPaginated] = useGetCitiesPaginatedLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [backendCityOptions, setBackendCityOptions] = useState<Option[]>([]);
    const [citySearchValue, setCitySearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState<number | null>(null);
    const [, setTotalCount] = useState<number | null>(null);
    const [isCitiesInitialLoading, setIsCitiesInitialLoading] = useState(false);
    const [isCitiesSearchLoading, setIsCitiesSearchLoading] = useState(false);
    const [isCitiesLoadingMore, setIsCitiesLoadingMore] = useState(false);
    const latestRequestIdRef = useRef(0);
    const hasOpenedMenuRef = useRef(false);
    const latestSearchValueRef = useRef("");
    const inFlightPageKeysRef = useRef(new Set<string>());
    const loadedPageKeysRef = useRef(new Set<string>());

    const selectedCityOption = useMemo(() => {
        return selectedCity ? mapCityToOption(selectedCity) : null;
    }, [selectedCity]);

    const hasMoreCities = useMemo(() => {
        return pageCount === null ? false : currentPage < pageCount;
    }, [currentPage, pageCount]);

    const cityOptions = useMemo(() => {
        const hasActiveSearch = citySearchValue.trim().length > 0;
        const optionsWithSelectedCity =
            selectedCityOption && !hasActiveSearch
                ? mergeUniqueOptions([selectedCityOption, ...backendCityOptions])
                : backendCityOptions;

        return appendCustomCityOption(
            optionsWithSelectedCity,
            includeCustomCityOption,
            customCityOptionLabel,
            customCityOptionValue
        );
    }, [
        backendCityOptions,
        citySearchValue,
        customCityOptionLabel,
        customCityOptionValue,
        includeCustomCityOption,
        selectedCityOption,
    ]);

    const updatePaginationState = useCallback(
        (currentResponsePage: number | null, responsePageCount: string | null, responseTotalCount: number | null) => {
            setCurrentPage(currentResponsePage ?? 0);
            setPageCount(responsePageCount ? Number(responsePageCount) : 0);
            setTotalCount(responseTotalCount ?? null);
        },
        []
    );

    const loadCitiesPage = useCallback(
        async (
            page: number,
            searchValue: string,
            shouldAppend: boolean,
            loadingType: "initial" | "search" | "more"
        ) => {
            if (!countryId || skip) {
                return;
            }

            const normalizedSearchValue = searchValue.trim();
            const pageKey = `${countryId}:${page}:${normalizedSearchValue}`;

            if (inFlightPageKeysRef.current.has(pageKey) || loadedPageKeysRef.current.has(pageKey)) {
                return;
            }

            const requestId = latestRequestIdRef.current + 1;
            latestRequestIdRef.current = requestId;
            inFlightPageKeysRef.current.add(pageKey);

            if (loadingType === "initial") {
                setIsCitiesInitialLoading(true);
            }
            if (loadingType === "search") {
                setIsCitiesSearchLoading(true);
            }
            if (loadingType === "more") {
                setIsCitiesLoadingMore(true);
            }

            try {
                const response = await getCitiesPaginated({
                    variables: {
                        countryId,
                        page,
                        itemsPerPage,
                        search: normalizedSearchValue || null,
                    },
                });

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                const citiesPaginated = response.data?.countryGeo?.citiesPaginated;
                const nextOptions = (citiesPaginated?.items ?? []).map(mapCityToOption);

                setBackendCityOptions((previousOptions) =>
                    shouldAppend ? mergeUniqueOptions([...previousOptions, ...nextOptions]) : nextOptions
                );
                updatePaginationState(
                    citiesPaginated?.currentPage ?? page,
                    citiesPaginated?.pageCount ?? null,
                    citiesPaginated?.totalCount ?? null
                );
                loadedPageKeysRef.current.add(pageKey);
            } catch (error) {
                if (requestId === latestRequestIdRef.current) {
                    console.error("Error loading cities:", error);
                }
            } finally {
                inFlightPageKeysRef.current.delete(pageKey);

                if (requestId === latestRequestIdRef.current) {
                    setIsCitiesInitialLoading(false);
                    setIsCitiesSearchLoading(false);
                    setIsCitiesLoadingMore(false);
                }
            }
        },
        [countryId, getCitiesPaginated, itemsPerPage, skip, updatePaginationState]
    );

    const resetCities = useCallback(() => {
        latestRequestIdRef.current += 1;
        hasOpenedMenuRef.current = false;
        latestSearchValueRef.current = "";
        inFlightPageKeysRef.current.clear();
        loadedPageKeysRef.current.clear();
        setBackendCityOptions([]);
        setCitySearchValue("");
        setCurrentPage(0);
        setPageCount(null);
        setTotalCount(null);
        setIsCitiesInitialLoading(false);
        setIsCitiesSearchLoading(false);
        setIsCitiesLoadingMore(false);
    }, []);

    const onCityMenuClose = useCallback(() => {
        if (citySearchValue.trim() === "" && latestSearchValueRef.current === "") {
            return;
        }
        resetCities();
    }, [citySearchValue, resetCities]);

    const onCityMenuOpen = useCallback(() => {
        hasOpenedMenuRef.current = true;

        if (!countryId || skip || currentPage > 0 || isCitiesInitialLoading) {
            return;
        }

        void loadCitiesPage(FIRST_PAGE, citySearchValue, false, "initial");
    }, [citySearchValue, countryId, currentPage, isCitiesInitialLoading, loadCitiesPage, skip]);

    const onCitySearchChange = useCallback(
        (inputValue: string, actionMeta: InputActionMeta) => {
            if (actionMeta.action !== "input-change") {
                return inputValue;
            }

            latestRequestIdRef.current += 1;
            inFlightPageKeysRef.current.clear();
            loadedPageKeysRef.current.clear();
            setCitySearchValue(inputValue);
            setCurrentPage(0);
            setPageCount(null);
            setTotalCount(null);
            if (countryId && !skip) {
                setIsCitiesSearchLoading(true);
            }

            return inputValue;
        },
        [countryId, skip]
    );

    const onCityMenuScrollToBottom = useCallback(() => {
        if (
            !countryId ||
            skip ||
            !hasMoreCities ||
            isCitiesLoadingMore ||
            isCitiesInitialLoading ||
            isCitiesSearchLoading
        ) {
            return;
        }

        void loadCitiesPage(currentPage + 1, latestSearchValueRef.current, true, "more");
    }, [
        countryId,
        currentPage,
        hasMoreCities,
        isCitiesInitialLoading,
        isCitiesLoadingMore,
        isCitiesSearchLoading,
        loadCitiesPage,
        skip,
    ]);

    useEffect(() => {
        if (!hasOpenedMenuRef.current || !countryId || skip) {
            return;
        }

        const searchTimeoutId = setTimeout(() => {
            latestSearchValueRef.current = citySearchValue;
            setCurrentPage(0);
            setPageCount(null);
            setTotalCount(null);
            void loadCitiesPage(FIRST_PAGE, citySearchValue, false, "search");
        }, SEARCH_DEBOUNCE_DELAY);

        return () => {
            clearTimeout(searchTimeoutId);
        };
    }, [citySearchValue, countryId, loadCitiesPage, skip]);

    useEffect(() => {
        resetCities();
    }, [countryId, resetCities]);

    return {
        cityOptions,
        selectedCityOption,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        isCitiesLoadingMore,
        hasMoreCities,
        citySearchValue,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    };
};
